/**
 * The Ledger tab's view models (dashboard spec §2.2 tab 7, W4 card C4): Finances, Standing (every faction), Law and
 * Corporation. Pure over the Career, the strategic relations and claim views, `captainView` and `corpView()`; read
 * only, no new save field. Runs at the dashboard refresh rate while it is open, never per frame.
 *
 * Standing trend and last change come from a session tracker (`createStandingTracker`) fed by the same refresh: no
 * save field keeps a standing history, so the cause of a change is not known and a fresh session starts flat.
 */
import type { Career, LeaseEventKind } from '../../../../career/types'
import { SIM_FACTION_IDS, type SimFactionId } from '../../../../data/m2Schemas'
import { formatCr, formatDuration, formatStanding } from '../../../format'
import { FACTION_IDS, PLAYER_FACTION_ID, type CaptainView, type CorpFactionId, type CorpView, type FactionId, type RelationsView } from '../../../../strategic/types'
import type { LedgerConfig, Tone } from './config'

function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m)
}

export interface LedgerRow {
  readonly label: string
  readonly value: string
  readonly tone: Tone
}

// ---------------------------------------------------------------------------------------------
// Finances
// ---------------------------------------------------------------------------------------------

export interface HistoryRow {
  readonly kind: LeaseEventKind
  readonly label: string
  readonly amount: string
  readonly ago: string
}

export interface FinancesModel {
  readonly rows: readonly LedgerRow[]
  readonly history: readonly HistoryRow[]
  /** The lease is still being paid (Pay lease shows). */
  readonly leaseOpen: boolean
}

/** Credits owed now: arrears plus late fees. */
function owedNow(career: Career): number {
  return career.lease.arrears + career.lease.feesDue
}

export function financesModel(cfg: LedgerConfig, career: Career, captains: CaptainView | null): FinancesModel {
  const t = cfg.finances
  const l = career.lease
  const now = career.gameTime
  const rows: LedgerRow[] = [{ label: t.wallet, value: formatCr(career.wallet), tone: 'neutral' }]
  if (l.owned) {
    rows.push({ label: t.principal, value: t.owned, tone: 'ok' })
  } else {
    rows.push({ label: t.principal, value: formatCr(l.principal), tone: 'neutral' })
    const left = l.nextDueAt - now
    const when = left <= 0 ? t.dueNow : fill(t.dueIn, { time: formatDuration(left) })
    rows.push({ label: t.nextDue, value: fill(t.nextDueValue, { amount: formatCr(l.payment), when }), tone: left <= 0 ? 'warn' : 'neutral' })
    const owed = owedNow(career)
    if (owed > 0) rows.push({ label: t.arrears, value: formatCr(owed), tone: 'warn' })
  }
  rows.push({ label: t.collections, value: t.collectionsState[l.collections], tone: l.collections === 'none' ? 'ok' : l.collections === 'notice' ? 'warn' : 'danger' })
  if (career.debt > 0) rows.push({ label: t.insurerDebt, value: formatCr(career.debt), tone: 'warn' })
  const hired = captains?.hired ?? []
  if (hired.length === 0) rows.push({ label: t.wageBill, value: t.wageBillNone, tone: 'neutral' })
  else {
    let bill = 0
    for (const c of hired) bill += c.wage
    rows.push({ label: t.wageBill, value: fill(t.wageBillValue, { amount: formatCr(bill), n: String(hired.length) }), tone: 'neutral' })
  }
  const history: HistoryRow[] = []
  for (let i = l.history.length - 1; i >= 0 && history.length < cfg.historyRows; i--) {
    const e = l.history[i]
    history.push({ kind: e.kind, label: t.kinds[e.kind], amount: formatCr(e.amount), ago: fill(t.historyAgo, { ago: formatDuration(now - e.t) }) })
  }
  return { rows, history, leaseOpen: !l.owned }
}

// ---------------------------------------------------------------------------------------------
// Standing
// ---------------------------------------------------------------------------------------------

export type Trend = 'up' | 'down' | 'flat'

export interface StandingChange {
  readonly delta: number
  /** Career game seconds. */
  readonly at: number
}

/** Session-only memory of each faction's last observed standing change (not saved). */
export interface StandingTracker {
  observe(standings: Readonly<Record<SimFactionId, number>>, now: number): void
  last(f: SimFactionId): StandingChange | null
  reset(): void
}

export function createStandingTracker(): StandingTracker {
  let prev: Partial<Record<SimFactionId, number>> | null = null
  const last = new Map<SimFactionId, StandingChange>()
  return {
    observe(standings, now): void {
      if (prev !== null) {
        for (const f of SIM_FACTION_IDS) {
          const p = prev[f]
          const v = standings[f]
          if (p !== undefined && v !== p) last.set(f, { delta: v - p, at: now })
        }
      }
      prev = { ...standings }
    },
    last: (f) => last.get(f) ?? null,
    reset(): void {
      prev = null
      last.clear()
    },
  }
}

export interface StandingRowModel {
  readonly id: SimFactionId
  readonly name: string
  readonly value: string
  readonly tier: string
  readonly tone: Tone
  readonly trend: Trend
  readonly last: string
}

/** The tier a standing value sits in (the highest `min` it reaches). */
export function standingTier(cfg: LedgerConfig['standing'], value: number): LedgerConfig['standing']['tiers'][number] {
  let tier = cfg.tiers[0]
  for (const t of cfg.tiers) if (value >= t.min) tier = t
  return tier
}

export function standingModel(cfg: LedgerConfig, career: Career, tracker: StandingTracker, nameOf: (f: SimFactionId) => string): StandingRowModel[] {
  const t = cfg.standing
  return SIM_FACTION_IDS.map((f) => {
    const v = career.standings[f]
    const tier = standingTier(t, v)
    const ch = tracker.last(f)
    const trend: Trend = ch === null ? 'flat' : ch.delta > 0 ? 'up' : 'down'
    const last = ch === null ? t.noChange : fill(t.lastChange, { delta: formatStanding(ch.delta), ago: formatDuration(career.gameTime - ch.at) })
    return { id: f, name: nameOf(f), value: formatStanding(v), tier: tier.label, tone: tier.tone, trend, last }
  })
}

// ---------------------------------------------------------------------------------------------
// Law
// ---------------------------------------------------------------------------------------------

export interface LawClaim {
  readonly holder: CorpFactionId | null
  readonly corpName?: string
}

export interface BountyRow {
  readonly id: FactionId
  readonly name: string
  readonly bounty: string
  readonly wanted: string
}

export interface LawModel {
  readonly jurisdiction: LedgerRow
  readonly status: LedgerRow
  readonly bounties: readonly BountyRow[]
  /** Credits to clear every bounty (`fines.mult × Σ bounty`); 0 = nothing owed. */
  readonly finesOwed: number
  readonly finesText: string
}

export interface LawInputs {
  /** The current system's claim; undefined = not in a system. */
  readonly claim: LawClaim | null | undefined
  readonly relations: RelationsView | null
  /** law.json `fines.mult`. */
  readonly finesMult: number
  /** Is a faction an outlaw (never a jurisdiction)? */
  isOutlaw(f: FactionId): boolean
  factionName(f: FactionId): string
}

export function lawModel(cfg: LedgerConfig, inp: LawInputs): LawModel {
  const t = cfg.law
  const holder = inp.claim?.holder ?? null
  let jurisdiction: LedgerRow
  let wantedHere = 0
  if (inp.claim === undefined) jurisdiction = { label: t.jurisdiction, value: t.unknown, tone: 'neutral' }
  else if (holder === PLAYER_FACTION_ID) jurisdiction = { label: t.jurisdiction, value: fill(t.corpClaim, { name: inp.claim?.corpName ?? '' }), tone: 'ok' }
  else if (holder === null || inp.isOutlaw(holder)) jurisdiction = { label: t.jurisdiction, value: t.lawless, tone: 'warn' }
  else {
    jurisdiction = { label: t.jurisdiction, value: fill(t.claim, { name: inp.factionName(holder) }), tone: 'neutral' }
    wantedHere = inp.relations?.player.wanted[holder] ?? 0
  }
  const status: LedgerRow = wantedHere > 0 ? { label: t.status, value: fill(t.wanted, { level: String(wantedHere) }), tone: 'danger' } : { label: t.status, value: t.clean, tone: 'ok' }
  const bounties: BountyRow[] = []
  let sum = 0
  for (const f of FACTION_IDS) {
    const b = inp.relations?.player.bounties[f] ?? 0
    const w = inp.relations?.player.wanted[f] ?? 0
    if (b <= 0 && w <= 0) continue
    sum += b
    bounties.push({ id: f, name: inp.factionName(f), bounty: formatCr(b), wanted: w > 0 ? fill(t.wanted, { level: String(w) }) : '—' })
  }
  const finesOwed = sum * inp.finesMult
  return { jurisdiction, status, bounties, finesOwed, finesText: formatCr(finesOwed) }
}

// ---------------------------------------------------------------------------------------------
// Corporation
// ---------------------------------------------------------------------------------------------

export type CorpModel =
  | { readonly state: 'loading'; readonly text: string }
  | { readonly state: 'none'; readonly text: string; readonly needs: string }
  | {
      readonly state: 'founded'
      readonly title: string
      readonly rows: readonly LedgerRow[]
      readonly income: readonly LedgerRow[]
      readonly costs: readonly LedgerRow[]
      readonly claims: readonly LedgerRow[]
      readonly milestones: readonly LedgerRow[]
    }

function signedTone(v: number): Tone {
  return v > 0 ? 'ok' : v < 0 ? 'danger' : 'neutral'
}

/** "first_extra_ship" / "firstExtraShip" → "First extra ship". */
export function humanize(id: string): string {
  const s = id.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)
}

export function corpModel(cfg: LedgerConfig, corp: CorpView | null, systemName: (id: string) => string): CorpModel {
  const t = cfg.corporation
  if (corp === null) return { state: 'loading', text: t.loading }
  if (!corp.founded) return { state: 'none', text: t.notFounded, needs: t.foundingNeeds }
  const pnl = (l: { label: string; today: number; yesterday: number }): LedgerRow => ({
    label: l.label,
    value: fill(t.pnlValue, { today: formatCr(l.today), yesterday: formatCr(l.yesterday) }),
    tone: signedTone(l.today),
  })
  const rows: LedgerRow[] = [
    { label: t.treasury, value: formatCr(corp.treasury), tone: corp.treasury < 0 ? 'danger' : 'neutral' },
    { label: t.net, value: fill(t.netValue, { today: formatCr(corp.net.today), yesterday: formatCr(corp.net.yesterday) }), tone: signedTone(corp.net.today) },
  ]
  if (corp.foundedDay !== null) rows.push({ label: t.foundedDay, value: fill(t.foundedDayValue, { day: String(corp.foundedDay) }), tone: 'neutral' })
  const claims = corp.claims.map(
    (c): LedgerRow => ({
      label: systemName(c.systemId),
      value: fill(t.claimValue, { tariff: `${Math.round(c.tariff * 100)}%`, docking: humanize(c.docking), score: String(Math.round(c.score)) }),
      tone: 'neutral',
    }),
  )
  const milestones = corp.milestones.map(
    (m): LedgerRow => ({
      label: humanize(m.id),
      value: m.day === null ? t.milestoneOpen : fill(t.milestoneDone, { day: String(m.day) }),
      tone: m.day === null ? 'neutral' : 'ok',
    }),
  )
  const title = corp.ticker !== '' ? `${corp.name} [${corp.ticker}]` : corp.name
  return { state: 'founded', title, rows, income: corp.income.map(pnl), costs: corp.costs.map(pnl), claims, milestones }
}
